import { useEffect, useState } from "react";
import {
  doc,
  serverTimestamp,
  setDoc,
  collection,
  query,
  orderBy,
  onSnapshot,
  where,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";

const MAX_CHARS = 280;
const MAX_IMAGE_SIZE = 500 * 1024; // 0.5 MB

function ProgressFeed() {
  const { user, profile } = useAuth();

  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");

  const [postedToday, setPostedToday] = useState(false);
  const [postStatusLoading, setPostStatusLoading] = useState(true);

  const [posts, setPosts] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);

  const [likeCounts, setLikeCounts] = useState({});

  const today = new Date().toISOString().slice(0, 10);
  const postId = user ? `${user.uid}_${today}` : null;

  useEffect(() => {
    if (!user?.uid || !postId) return;
    setPostStatusLoading(true);

    const refDoc = doc(db, "progressPosts", postId);
    const unsub = onSnapshot(refDoc, (snap) => {
      setPostedToday(snap.exists());
      setPostStatusLoading(false);
    });

    return () => unsub();
  }, [user?.uid, postId]);

  useEffect(() => {
    if (!user?.uid) return;
    setFeedLoading(true);

    const q = query(
      collection(db, "progressPosts"),
      where("founderId", "==", user.uid),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPosts(data);
      setFeedLoading(false);
    });

    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    if (posts.length === 0) {
      setLikeCounts({});
      return;
    }

    const unsubscribers = posts.map((p) =>
      onSnapshot(collection(db, "progressPosts", p.id, "likes"), (snap) => {
        setLikeCounts((prev) => ({ ...prev, [p.id]: snap.size }));
      })
    );

    return () => unsubscribers.forEach((u) => u());
  }, [posts]);

  const handleImageChange = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Only image files are allowed.");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setError("Image must be under 0.5 MB.");
      return;
    }
    setError("");
    setImage(file);
  };

  const formatDate = (timestamp) => {
    if (!timestamp?.toDate) return "";
    const date = timestamp.toDate();
    const todayDate = new Date();
    const yesterday = new Date();
    yesterday.setDate(todayDate.getDate() - 1);
    if (date.toDateString() === todayDate.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handlePost = async () => {
    if (!text.trim()) return setError("Post cannot be empty.");
    if (text.length > MAX_CHARS)
      return setError("Post exceeds character limit.");

    try {
      setPosting(true);
      setError("");
      let imageUrl = null;

      if (image) {
        const imageRef = ref(storage, `progress-feed/${user.uid}/${postId}`);
        await uploadBytes(imageRef, image);
        imageUrl = await getDownloadURL(imageRef);
      }

      await setDoc(doc(db, "progressPosts", postId), {
        founderId: user.uid,
        founderName: profile.fullName,
        startupName: profile.startupName,
        text: text.trim(),
        imageUrl: imageUrl || null,
        createdDate: today,
        createdAt: serverTimestamp(),
      });

      setText("");
      setImage(null);
    } catch (err) {
      console.error(err);
      setError("Failed to post update.");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div>
      <h1
        title="Progress Feed lets evaluators see your startup’s momentum over time."
      >
        Progress Feed
      </h1>

      <p
        style={{ marginTop: 8, color: "#64748b" }}
        title="You can post one update per day. These updates are visible to evaluators."
      >
        Share one update per day about what’s new with your startup.
      </p>

      {/* POST CARD */}
      <div
        className="card"
        style={{
          padding: 28,
          marginTop: 24,
          maxWidth: 720,
          background: "linear-gradient(135deg, #f8fafc, #eef2ff)",
          border: "1px solid #e2e8f0",
        }}
        title="Daily progress updates help evaluators understand consistency, execution, and momentum."
      >
        <h3
          style={{ marginBottom: 12 }}
          title="You can post only once per day. Focus on meaningful progress."
        >
          Post today’s update
        </h3>

        {postStatusLoading ? (
          <p style={{ color: "#94a3b8" }}>Checking today’s status…</p>
        ) : postedToday ? (
          <p
            style={{ color: "#16a34a" }}
            title="You can post again tomorrow."
          >
            ✅ You’ve already posted today.
          </p>
        ) : (
          <>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What progress did you make today?"
              rows={4}
              style={{
                width: "100%",
                marginTop: 12,
                padding: 14,
                borderRadius: 12,
                border: "1px solid #cbd5e1",
                fontSize: 14,
              }}
              title="Good updates include milestones, learnings, launches, experiments, or traction."
            />

            <div
              style={{ fontSize: 13, marginTop: 6, color: "#64748b" }}
              title="Maximum 280 characters."
            >
              {text.length}/{MAX_CHARS}
            </div>

            {/* Upload Image */}
            <label
              style={{
                display: "inline-block",
                marginTop: 14,
                padding: "8px 14px",
                borderRadius: 999,
                background: "#6366f1",
                color: "#fff",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
              }}
              title="Optional. Upload a small image (screenshot, chart, photo). Max size 0.5 MB."
            >
              Upload Image
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handleImageChange(e.target.files[0])}
              />
            </label>

            {error && <p style={{ color: "#dc2626", marginTop: 8 }}>{error}</p>}

            <button
              className="btn-primary"
              onClick={handlePost}
              disabled={posting}
              style={{
                marginTop: 18,
                display: "block",
                padding: "10px 22px",
                borderRadius: 999,
                fontWeight: 600,
                fontSize: 14,
              }}
              title="Once posted, this update cannot be edited."
            >
              {posting ? "Posting..." : "Post Update"}
            </button>
          </>
        )}
      </div>

      {/* FEED */}
      <div style={{ marginTop: 32, maxWidth: 720 }}>
        <h3
          style={{ marginBottom: 16 }}
          title="These are your past updates, visible to evaluators."
        >
          Recent Updates
        </h3>

        {feedLoading ? (
          <p style={{ color: "#94a3b8" }}>Loading updates…</p>
        ) : posts.length === 0 ? (
          <p style={{ color: "#94a3b8" }}>
            No updates yet.
          </p>
        ) : (
          posts.map((p) => (
            <div
              key={p.id}
              className="card"
              style={{
                padding: 20,
                marginBottom: 20,
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
              title="Evaluators can like updates they find meaningful."
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "translateY(-4px)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "translateY(0)")
              }
            >
              <strong>{p.startupName}</strong>
              <div
                style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}
                title="When this update was posted."
              >
                {formatDate(p.createdAt)}
              </div>

              <p style={{ marginTop: 10, lineHeight: 1.6 }}>
                {p.text}
              </p>

              {p.imageUrl && (
                <img
                  src={p.imageUrl}
                  alt="Progress"
                  style={{
                    marginTop: 12,
                    width: "100%",
                    borderRadius: 14,
                    maxHeight: 420,
                    objectFit: "cover",
                  }}
                  title="Attached image for this update."
                />
              )}

              {likeCounts[p.id] > 0 && (
                <div
                  style={{
                    marginTop: 12,
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#e11d48",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                  title="Likes indicate evaluator interest or appreciation."
                >
                  ❤️ {likeCounts[p.id]} like{likeCounts[p.id] > 1 ? "s" : ""}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ProgressFeed;
